import { useState } from "react";
import { PackageSearch, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, STATUS_STEPS } from "@/lib/constants";
import Reveal from "@/components/Reveal";

const Tracking = () => {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrder(null);
    try {
      const res = await api.get("/orders/track", { params: { code, email } });
      setOrder(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = order ? STATUS_STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-8">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Suivi de commande</p>
        <h1 className="mt-2 font-serif text-3xl text-[#1A1714] sm:text-4xl">Où en est ma commande ?</h1>
        <p className="mt-3 text-sm text-[#5C5449]">
          Entrez le code reçu lors de votre commande (ex : ANM-A1B2C3) et l'email utilisé.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <form onSubmit={submit} className="mt-8 flex flex-col gap-3 rounded-2xl border border-[#EADFCB] bg-white p-5 sm:flex-row">
          <input
            data-testid="order-tracking-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            placeholder="Code de commande"
            className="flex-1 rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]"
          />
          <input
            data-testid="order-tracking-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="Votre email"
            className="flex-1 rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]"
          />
          <button
            data-testid="order-tracking-submit-button"
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#C59B27] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95 disabled:opacity-50"
          >
            <PackageSearch size={16} /> {loading ? "Recherche…" : "Suivre"}
          </button>
        </form>
      </Reveal>

      {order && (
        <Reveal className="mt-8 rounded-3xl border border-[#EADFCB] bg-white p-7" >
          <div data-testid="tracking-result">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#C59B27]">Commande</p>
                <p className="font-accent text-2xl tracking-[0.15em] text-[#1A1714]">{order.code}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#5C5449]">{new Date(order.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                <p className="font-serif text-xl font-bold text-[#1A1714]">{formatPrice(order.total)}</p>
              </div>
            </div>

            <div className="mt-8">
              {STATUS_STEPS.map((s, i) => (
                <div key={s.key} className="flex gap-4" data-testid={`tracking-step-${s.key}`}>
                  <div className="flex flex-col items-center">
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                        i <= currentIdx ? "border-[#C59B27] bg-[#C59B27] text-white" : "border-[#EADFCB] bg-white text-[#D9C9A8]"
                      }`}
                    >
                      {i <= currentIdx ? <Check size={15} /> : <span className="text-xs font-bold">{i + 1}</span>}
                    </span>
                    {i < STATUS_STEPS.length - 1 && (
                      <span className={`h-8 w-0.5 ${i < currentIdx ? "bg-[#C59B27]" : "bg-[#EADFCB]"}`} />
                    )}
                  </div>
                  <div className="pb-8 pt-1.5">
                    <p className={`text-sm font-semibold ${i <= currentIdx ? "text-[#1A1714]" : "text-[#5C5449]/50"}`}>{s.label}</p>
                    {i === currentIdx && <p className="text-xs font-medium text-[#C59B27]">Étape en cours</p>}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2 space-y-2 border-t border-[#EADFCB] pt-4">
              {order.items.map((i) => (
                <div key={`${i.perfume_id}-${i.size}`} className="flex justify-between text-sm">
                  <span className="text-[#5C5449]">{i.name} ({i.brand}) — {i.size} ml × {i.qty}</span>
                  <span className="font-semibold text-[#1A1714]">{formatPrice(i.unit_price * i.qty)}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}
    </div>
  );
};

export default Tracking;
